import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Receipt, Search, Download, ExternalLink, Copy as CopyIcon, Check, Filter,
  Store, Smartphone, Calendar, IndianRupee, Tag,
} from "lucide-react";

const CAT = { promoter: "bg-emerald-100 text-emerald-700", passive: "bg-amber-100 text-amber-700", detractor: "bg-rose-100 text-rose-700" };

export default function TransactionsPage() {
  const [txns, setTxns] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | responded | pending
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/transactions");
        setTxns(r.data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = txns
    .filter((t) => {
      if (filter === "responded") return !!t.response_id;
      if (filter === "pending") return !t.response_id;
      return true;
    })
    .filter((t) => {
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return [t.ref_id, t.mobile_number, t.bill_number, t.store_code, t.bill_type]
        .filter(Boolean).some((x) => String(x).toLowerCase().includes(s));
    });

  const stats = {
    total: txns.length,
    responded: txns.filter((t) => t.response_id).length,
    pending: txns.filter((t) => !t.response_id).length,
    avgNps: (() => {
      const scored = txns.filter((t) => typeof t.nps_score === "number");
      if (scored.length === 0) return null;
      const prom = scored.filter((t) => t.nps_score >= 9).length;
      const det = scored.filter((t) => t.nps_score <= 6).length;
      return Math.round(((prom - det) / scored.length) * 100);
    })(),
  };

  const copy = (txt, marker) => {
    navigator.clipboard.writeText(txt);
    setCopied(marker);
    setTimeout(() => setCopied(""), 1500);
  };

  const exportCsv = () => {
    if (!filtered.length) return;
    const headers = ["received_at", "ref_id", "mobile_number", "bill_number", "store_code", "bill_date", "bill_amount", "bill_type", "responded", "nps_score", "nps_category", "survey_url"];
    const rows = filtered.map((t) => [
      t.created_at, t.ref_id, t.mobile_number || "", t.bill_number || "",
      t.store_code || "", t.bill_date || "", t.bill_amount ?? "", t.bill_type || "",
      t.response_id ? "yes" : "no", t.nps_score ?? "", t.nps_category || "",
      `"${t.survey_url || ""}"`,
    ].map((v) => String(v).replace(/,/g, " ")).join(","));
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = "transactions.csv"; a.click();
    URL.revokeObjectURL(u);
  };

  return (
    <div className="space-y-5 fade-in">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-[#1F3A6B]">Transactions</h2>
          <p className="text-sm text-slate-500 mt-1">Customer transactions ingested via your POS / billing API key.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/settings">
            <Button variant="outline" data-testid="goto-api-keys"><ExternalLink size={14} className="mr-1.5"/>API Keys</Button>
          </Link>
          <Button data-testid="txn-export-csv" onClick={exportCsv} variant="outline" disabled={!filtered.length}><Download size={14} className="mr-1.5"/>Export</Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total transactions" value={stats.total} icon={<Receipt size={16}/>} tone="brand"/>
        <KpiCard label="Responded" value={stats.responded} sub={stats.total ? `${Math.round((stats.responded / stats.total) * 100)}%` : "—"} icon={<Check size={16}/>} tone="emerald"/>
        <KpiCard label="Pending" value={stats.pending} icon={<Filter size={16}/>} tone="amber"/>
        <KpiCard label="NPS (responded)" value={stats.avgNps === null ? "—" : stats.avgNps} icon={<Tag size={16}/>} tone={stats.avgNps !== null && stats.avgNps >= 30 ? "emerald" : stats.avgNps !== null && stats.avgNps >= 0 ? "amber" : "rose"}/>
      </div>

      {/* Filter bar */}
      <Card className="p-3 border-slate-100">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input
              data-testid="txn-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by ref_id, mobile, bill no, store code…"
              className="h-10 pl-9"
            />
          </div>
          {["all", "responded", "pending"].map((f) => (
            <button
              key={f}
              data-testid={`txn-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === f ? "bg-[#F58220] text-white border-[#F58220]" : "bg-white border-slate-200 text-slate-600 hover:border-[#F58220]"}`}
            >
              {f === "all" ? `All (${txns.length})` : f === "responded" ? `Responded (${stats.responded})` : `Pending (${stats.pending})`}
            </button>
          ))}
        </div>
      </Card>

      {/* List */}
      <Card className="border-slate-100 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState hasAny={txns.length > 0}/>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Received</th>
                <th className="text-left px-4 py-3 font-semibold">Ref ID</th>
                <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                <th className="text-left px-4 py-3 font-semibold">Bill</th>
                <th className="text-left px-4 py-3 font-semibold">Store</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Link</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} data-testid={`txn-row-${t.id}`} className="border-t border-slate-100 hover:bg-slate-50/40">
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600 tabular-nums">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#1F3A6B]">{t.ref_id}</td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums">{t.mobile_number || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{t.bill_number || "—"}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">{t.store_code || "—"}</span></td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-[#1F3A6B]">{t.bill_amount !== null && t.bill_amount !== undefined ? `₹${Number(t.bill_amount).toFixed(2)}` : "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{t.bill_type || "—"}</td>
                  <td className="px-4 py-3">
                    {t.response_id ? (
                      <div className="flex items-center gap-1.5">
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Responded</Badge>
                        {typeof t.nps_score === "number" && (
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${CAT[t.nps_category] || "bg-slate-100 text-slate-700"}`}>
                            {t.nps_score}
                          </span>
                        )}
                      </div>
                    ) : (
                      <Badge variant="outline" className="text-slate-600 border-slate-200">Pending</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {t.survey_url ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => copy(t.survey_url, t.id)} className="text-[#F58220] hover:underline text-xs font-semibold flex items-center gap-1" title="Copy link">
                          <CopyIcon size={11}/>{copied === t.id ? "Copied" : "Copy link"}
                        </button>
                        <a href={t.survey_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#1F3A6B]" title="Open survey">
                          <ExternalLink size={12}/>
                        </a>
                      </div>
                    ) : <span className="text-xs text-slate-400">No survey</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function KpiCard({ label, value, sub, icon, tone }) {
  const toneClass = {
    brand: "text-[#1F3A6B]",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
  }[tone] || "text-[#1F3A6B]";
  return (
    <Card className="p-4 border-slate-100" data-testid={`txn-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
        <span className={toneClass}>{icon}</span>{label}
      </div>
      <p className={`mt-1.5 text-2xl font-extrabold tabular-nums ${toneClass}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </Card>
  );
}

function EmptyState({ hasAny }) {
  return (
    <div className="p-12 text-center">
      <Receipt size={32} className="mx-auto text-slate-300"/>
      <p className="mt-3 font-semibold text-[#1F3A6B]">{hasAny ? "No matches" : "No transactions yet"}</p>
      <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
        {hasAny
          ? "Try a different filter or search."
          : "Generate an API key in Settings and POST transactions from your POS to start populating this page."}
      </p>
      {!hasAny && (
        <Link to="/app/settings" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F3A6B] hover:underline">
          <ExternalLink size={14}/> Go to API Keys
        </Link>
      )}
    </div>
  );
}
